import { useEffect, useRef } from 'react';
import type { FilterState } from '@calendar/components/calendar-filters';

export interface UseScrollToTopOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  inline?: ScrollLogicalPosition;
  delay?: number;
  offset?: number; // Buffer in pixels above the target element
}

export function useScrollToTop(
  filters: FilterState,
  options: UseScrollToTopOptions = {}
) {
  const eventsContainerRef = useRef<HTMLDivElement>(null);
  const previousFiltersRef = useRef<FilterState>(filters);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // Skip scroll on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousFiltersRef.current = filters;
      return;
    }

    const currentFilters = filters;
    const previousFilters = previousFiltersRef.current;

    // Check if any filter has changed
    const hasFiltersChanged =
      currentFilters.season !== previousFilters.season ||
      currentFilters.month !== previousFilters.month ||
      currentFilters.category !== previousFilters.category ||
      currentFilters.livestreamOnly !== previousFilters.livestreamOnly ||
      currentFilters.ticketedOnly !== previousFilters.ticketedOnly ||
      currentFilters.upcomingOnly !== previousFilters.upcomingOnly ||
      currentFilters.view !== previousFilters.view;

    if (hasFiltersChanged && eventsContainerRef.current) {
      const scrollToTop = () => {
        if (!eventsContainerRef.current) return;

        if (options.offset && options.offset > 0) {
          // Custom scroll with offset
          const elementTop = eventsContainerRef.current.getBoundingClientRect().top;
          const offsetPosition = elementTop + window.pageYOffset - options.offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: options.behavior || 'smooth'
          });
        } else {
          // Standard scrollIntoView
          eventsContainerRef.current.scrollIntoView({
            behavior: options.behavior || 'smooth',
            block: options.block || 'start',
            inline: options.inline || 'nearest'
          });
        }
      };

      // Add optional delay for better UX (allows content to render first)
      if (options.delay && options.delay > 0) {
        setTimeout(scrollToTop, options.delay);
      } else {
        scrollToTop();
      }
    }

    // Update the previous filters reference
    previousFiltersRef.current = currentFilters;
  }, [ filters, options.behavior, options.block, options.inline, options.delay, options.offset ]);

  return eventsContainerRef;
}
